/**
 * Main Application Component
 * @module App
 */

import React, { Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Core imports
import { isBuildLocal } from './core/config/environment';

// Feature imports — AuthProvider + PrivateRoute stay eager (they wrap the app shell).
import { AuthProvider } from './features/auth/hooks/useAuth';
import { PrivateRoute } from './features/auth/components/PrivateRoute';

// Shared imports
import { LoadingProvider } from './shared';

// Layout imports
// PublicLayout dùng BẢN Ở layouts/, không dựng lại tại đây.
//
// Trước 03/09/2026 App.tsx có một PublicLayout riêng y hệt, còn
// layouts/PublicLayout.tsx thì nằm không — sửa vào file trong layouts/ chẳng
// thay đổi gì trên màn hình mà không có dấu hiệu nào báo, đúng kiểu bẫy đã gặp
// với src/interfaces/. Một bản duy nhất thì không lặp lại được chuyện đó.
import { PublicLayout } from './layouts';

import './styles/nb-base.scss';
import './styles/_mobile-modal-safe.scss';

// Route components are LAZY — each page ships as its own chunk so a visitor
// only downloads what the opened route needs. Critically, the public exam
// roster (/danh-sach-thi) no longer pulls in dashboard/chatbot/pdf/xlsx code.
const DashBoardRoute = React.lazy(() => import('./pages/DashBoardPage/DashBoardRoute'));
const LoginTestStudent = React.lazy(() => import('./features/exam/components/LoginTestStudent/LoginTestStudent'));
const LoginForm = React.lazy(() => import('./features/auth/components/LoginForm').then((m) => ({ default: m.LoginForm })));
const StudentsList = React.lazy(() => import('./features/student/components/StudentsList'));
const FinalExamForm = React.lazy(() => import('./features/exam/components/FinalExamForm'));
const ReviewPage = React.lazy(() => import('./features/review/components/ReviewPage'));
const ReviewChillPage = React.lazy(() => import('./features/review/components/ReviewChillPage'));
const RetakeFeeLookupPage = React.lazy(() => import('./features/retakeFee/components/RetakeFeeLookupPage'));
const PrivacyPolicyPage = React.lazy(() => import('./features/pdpl/components/PrivacyPolicyPage'));
const RefundPublicPage = React.lazy(() => import('./features/refund/components/RefundPublicPage'));
const ExamRosterPublicPage = React.lazy(() => import('./features/examRoster/components/ExamRosterPublicPage'));
const EmsParcelPublicPage = React.lazy(() => import('./features/emsParcel/components/EmsParcelPublicPage'));
const RefundRoundPublicPage = React.lazy(() => import('./features/refund/components/RefundRoundPublicPage'));
const RefundRoundListPage = React.lazy(() => import('./features/refund/components/RefundRoundListPage'));
const HomeLanding = React.lazy(() => import('./features/home/components/HomeLanding'));
const NewsListPage = React.lazy(() => import('./features/home/components/NewsListPage'));
const ExamHubPage = React.lazy(() => import('./features/home/components/ExamHubPage'));
// Ôn mô phỏng công khai — cùng màn hình với bản trong dashboard, bật cờ
// publicMode để giấu phần thi tính điểm (phần đó cần tài khoản học viên).
const MophongPracticePage = React.lazy(() => import('./features/mophong/components/MophongExamPage'));
const NewsDetailPage = React.lazy(() => import('./features/home/components/NewsDetailPage'));
const StudentPortal = React.lazy(() => import('./pages/StudentPortal/StudentPortal'));
const SetupPassword = React.lazy(() => import('./pages/SetupPassword/SetupPassword'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword/ForgotPassword'));
const TeacherPortal = React.lazy(() => import('./pages/TeacherPortal/TeacherPortal'));
const AiChatPage = React.lazy(() => import('./pages/DashBoardPage/AiChatPage/AiChatPage'));
const EmbedChatPage = React.lazy(() => import('./features/chatbotEmbed/EmbedChatPage'));
const ToolsLayout = React.lazy(() => import('./pages/PublicTools/ToolsLayout'));
const PublicToolsHub = React.lazy(() => import('./pages/PublicTools/PublicToolsHub'));
const PdfEditorPage = React.lazy(() => import('./features/pdfTools/components/editor/PdfEditorPage'));
const PdfSplitPage = React.lazy(() => import('./features/pdfTools/components/split/PdfSplitPage'));
const YoutubeDownloaderPage = React.lazy(() => import('./features/pdfTools/components/youtube/YoutubeDownloaderPage'));
const PhotoSheetPage = React.lazy(() => import('./features/pdfTools/components/photoSheet/PhotoSheetPage'));
const XmlPhotoBgPage = React.lazy(() => import('./features/xmlPhotoTool/components/XmlPhotoBgPage'));
const MusicFinderPage = React.lazy(() => import('./features/musicFinder/components/MusicFinderPage'));
const CommunityGamesHub = React.lazy(() => import('./features/communityGames/components/CommunityGamesHub'));
const FileSharePublicPage = React.lazy(() => import('./features/fileStore/components/PublicSharePage'));

/**
 * Dashboard Layout Wrapper
 */
const DashBoardLayoutWrapper: React.FC = () => {
  return (
    <div className="dashboard-layout">
      <DashBoardRoute />
    </div>
  );
};

// The only document other sites may iframe (server.js opens framing on this
// path alone). It renders the chat and nothing else, whatever hash the host
// page appends — so a framed copy can never show the dashboard or login.
const EMBED_CHAT_PATH = '/embed/chat';

const isEmbedChatDocument = (): boolean =>
  window.location.pathname.replace(/\/+$/, '') === EMBED_CHAT_PATH;

/**
 * Main App Component
 */
const App: React.FC = () => {
  const isLocal = isBuildLocal();

  if (isEmbedChatDocument()) {
    return (
      <Router>
        <Suspense fallback={null}>
          <EmbedChatPage />
        </Suspense>
      </Router>
    );
  }

  return (
    <>
      <ToastContainer />
      <AuthProvider>
        <LoadingProvider>
          <Router>
            <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Đang tải…</div>}>
            <Routes>
              {/* Standalone route for exam */}
              <Route path="finalexam" element={<FinalExamForm />} />

              {/* Standalone AI chat dialog — public, URL-only, no header/footer/auth */}
              <Route path="ai-chat" element={<AiChatPage />} />

              {/* Public exam-day roster — fixed shareable URL. Standalone (no
                  header/footer/auth) so candidates open it anonymously.
                  HashRouter → link is #/danh-sach-thi/:date. The param-less
                  variant auto-shows the nearest exam session. */}
              <Route path="danh-sach-thi" element={<ExamRosterPublicPage />} />
              <Route path="danh-sach-thi/:examDate" element={<ExamRosterPublicPage />} />

              {/* Danh sách hoàn tiền công khai — địa chỉ cố định chia sẻ được,
                  đứng riêng (không header/footer/đăng nhập) đúng như danh sách
                  sát hạch, để người dân mở thẳng từ link. */}
              <Route path="danh-sach-hoan-tien/:slug" element={<RefundRoundPublicPage />} />

              {/* Embeddable chatbot — 3rd party sites iframe this URL.
                  Public, no header/footer, no app shell. Query params:
                    ?title=...&primary=hex&placeholder=... */}
              <Route path="embed/chat" element={<EmbedChatPage />} />

              {/* TRA CỨU ĐIỂM & ĐÓNG PHÍ THI LẠI — đứng riêng, KHÔNG có header,
                  chân trang hay nút đăng nhập.

                  Trang này được nhúng vào Mezon: trong khung đó người dùng
                  không rời trang được, nên thanh header và nút "Đăng nhập" chỉ
                  là những lối bấm dẫn vào ngõ cụt. Giữ NGUYÊN địa chỉ cũ
                  (#/dong-phi-thi-lai) chứ không đẻ thêm địa chỉ nhúng riêng —
                  link đã phát ra ngoài rồi, đổi là hỏng hết chỗ đang dùng.

                  ĐÁNH ĐỔI: người vào từ chân trang website cũng mất thanh điều
                  hướng ở trang này; bù lại đúng một trang, và đó là trang người
                  ta vào để tra một việc rồi thôi.

                  Ba địa chỉ cũ bên dưới chuyển hướng về đây và cũng nằm ngoài
                  PublicLayout — để trong đó thì header kịp nhá lên một nhịp
                  trước khi chuyển. */}
              <Route path="dong-phi-thi-lai" element={<RetakeFeeLookupPage />} />
              <Route path="lookup" element={<Navigate to="/dong-phi-thi-lai" replace />} />
              <Route path="license-check" element={<Navigate to="/dong-phi-thi-lai" replace />} />
              <Route path="traffic-check" element={<Navigate to="/dong-phi-thi-lai" replace />} />

              {/* Public Layout Routes */}
              <Route path="/" element={<PublicLayout />}>
                {/* Public landing page. It advertises every public service and
                    links into the dashboard through the header, so there is no
                    redirect here any more. isLocal kiosk builds still open the
                    exam login at index. */}
                <Route
                  index
                  element={isLocal ? <LoginTestStudent /> : <HomeLanding />}
                />
                <Route path="tin-tuc" element={<NewsListPage />} />
                <Route path="tin-tuc/:slug" element={<NewsDetailPage />} />
                <Route path="teststudent" element={<LoginTestStudent />} />
                <Route path="danh-sach-hoan-tien" element={<RefundRoundListPage />} />
                <Route path="on-thi" element={<ExamHubPage />} />
                <Route path="on-mo-phong" element={<MophongPracticePage publicMode />} />
                <Route path="review" element={<ReviewPage />} />
                <Route path="review/chill/:setId" element={<ReviewChillPage />} />
                {/* Hoàn lệ phí sát hạch chưa nhận — công khai, không cần đăng nhập. */}
                <Route path="hoan-tien" element={<RefundPublicPage />} />

                {/* Tra cứu bưu phẩm GPLX — công khai. Cố ý nằm TRONG
                    PublicLayout (khác /danh-sach-thi): người vừa biết bằng của
                    mình đang trên đường thường còn muốn xem tiếp việc khác. */}
                <Route path="tra-cuu-buu-pham" element={<EmsParcelPublicPage />} />

                {/* Thư mục được chia sẻ của Kho tài liệu — ai có link cũng xem và tải,
                    không đăng nhập. Link sạch /kho/<mã> được server.js chuyển về đây. */}
                <Route path="kho/:token" element={<FileSharePublicPage />} />

                {/* PUBLIC tools — không đăng nhập, không chuyển hướng.
                    [2026-08-30] Nằm TRONG PublicLayout để dùng chung thanh
                    header: trước đây trang này có thanh bar riêng, vào đây là
                    mất hẳn đường sang Bảng tin / Ôn tập.
                    Công cụ XML sát hạch cố ý KHÔNG nằm đây (chỉ cho người đã
                    đăng nhập, khâu ký số dùng khoá riêng của trung tâm). */}
                <Route path="tools" element={<ToolsLayout />}>
                  <Route index element={<PublicToolsHub />} />
                  <Route path="pdf-split" element={<PdfSplitPage />} />
                  <Route path="photo-sheet" element={<PhotoSheetPage />} />
                  <Route path="pdf-editor" element={<PdfEditorPage />} />
                  <Route path="youtube" element={<YoutubeDownloaderPage />} />
                  <Route path="music-finder" element={<MusicFinderPage />} />
                  {/* Trò chơi cộng đồng — công khai: người dẫn chương trình
                      thường mở trên laptop mượn tại hội trường, bắt đăng nhập
                      là hỏng buổi. Không đụng backend, không lưu gì lên server. */}
                  <Route path="tro-choi" element={<CommunityGamesHub />} />
                  {/* Background conversion is public; the SIGN step inside is
                      login-gated (per-center FPT key) — handled in-page. */}
                  <Route path="xml-photo" element={<XmlPhotoBgPage />} />
                </Route>
                {/* Chính sách bảo vệ dữ liệu cá nhân (Luật 91/2025/QH15) —
                    trang công bố bắt buộc, kèm biểu mẫu để người dân thực hiện
                    quyền với dữ liệu của mình. Nằm trong PublicLayout để giữ
                    thanh điều hướng: người đọc xong thường cần quay lại trang
                    họ đang dùng dở. */}
                <Route path="chinh-sach-du-lieu" element={<PrivacyPolicyPage />} />
                <Route path="students" element={<StudentsList />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>

              {/* Login — standalone (no public nav header/footer) so the
                  dashboard entry flow has nothing else to click into */}
              <Route path="login" element={<LoginForm />} />

              {/* Student portal — accessible to any authenticated user */}
              <Route path="student-portal" element={<StudentPortal />} />

              {/* Setup password — public, one-time link from email */}
              <Route path="setup-password" element={<SetupPassword />} />

              {/* Forgot password — public */}
              <Route path="forgot-password" element={<ForgotPassword />} />

              {/* Teacher portal — authenticated teachers */}
              <Route path="teacher-portal" element={<TeacherPortal />} />

              {/* Private Dashboard Routes */}
              <Route
                path="dashboard/*"
                element={
                  <PrivateRoute requiredRole={['SupperAdmin', 'Admin', 'SupperTeacher', 'GiaoVien', 'HocVien', 'NhanVienTrungTam', 'NhanVienNhapLieu', 'SupperNhanVienNhapLieu', 'NhanVienQuanLyXe']}>
                    <DashBoardLayoutWrapper />
                  </PrivateRoute>
                }
              />
            </Routes>
            </Suspense>
          </Router>
        </LoadingProvider>
      </AuthProvider>
    </>
  );
};

export default App;
